import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { formatAUD, stripBasiqPrefix } from '@/lib/format';
import { TransactionType } from '@/lib/types';
import { ArrowLeft, Trash2, CreditCard, Receipt, Loader2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function TransactionDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, updateData } = useAuth();
  const navigate = useNavigate();
  
  // Decode the ID in case it was URL encoded
  const decodedId = id ? decodeURIComponent(id) : '';
  const txn = data.transactions.find(t => t.id === decodedId);

  const [type, setType] = useState<TransactionType>(txn?.type ?? 'expense');
  const [amount, setAmount] = useState(txn?.amount.toString() ?? '');
  const [category, setCategory] = useState(txn?.category ?? '');
  const [description, setDescription] = useState(stripBasiqPrefix(txn?.description ?? ''));
  const [date, setDate] = useState(txn?.date ?? '');
  const [taxDeductible, setTaxDeductible] = useState(txn?.taxDeductible ?? false);
  const [superContribution, setSuperContribution] = useState(txn?.superContribution?.toString() ?? '');
  const [accountId, setAccountId] = useState(txn?.accountId ?? '');
  const [saving, setSaving] = useState(false);

  if (!txn) {
    return (
      <div className="px-4 pt-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
        <p className="mt-4 text-muted-foreground">Transaction not found</p>
        <p className="text-xs text-muted-foreground mt-2">ID: {decodedId}</p>
      </div>
    );
  }

  const accounts = data.accounts ?? [];
  const isSynced = txn.description.startsWith('[basiq:');

  const handleSave = async () => {
    if (!amount || !category) return;
    setSaving(true);
    await updateData(prev => ({
      ...prev,
      transactions: prev.transactions.map(t =>
        t.id === decodedId
          ? {
              ...t,
              type,
              amount: parseFloat(amount),
              date,
              category,
              description: isSynced ? `[basiq:${txn.id.split(':')[1]?.replace(']', '') || txn.id}] ${description}` : description,
              taxDeductible,
              superContribution: superContribution ? parseFloat(superContribution) : undefined,
              accountId: accountId || undefined,
            }
          : t
      ),
    }));
    setSaving(false);
    toast.success('Transaction updated');
    navigate(-1);
  };

  const handleDelete = async () => {
    await updateData(prev => ({
      ...prev,
      transactions: prev.transactions.filter(t => t.id !== decodedId),
    }));
    toast.success('Transaction deleted');
    navigate('/transactions');
  };

  const handleAddAsBill = async () => {
    const cleanDesc = stripBasiqPrefix(txn.description);
    const newBill = {
      id: crypto.randomUUID(),
      name: cleanDesc || category,
      amount: txn.amount,
      dueDate: txn.date,
      frequency: 'monthly' as const,
      paid: false,
    };
    await updateData(prev => ({
      ...prev,
      bills: [...prev.bills, newBill],
    }));
    toast.success('Added as recurring bill');
  };

  const handleAddAsSubscription = async () => {
    await updateData(prev => ({
      ...prev,
      transactions: prev.transactions.map(t =>
        t.id === decodedId ? { ...t, category: 'Subscriptions' } : t
      ),
    }));
    setCategory('Subscriptions');
    toast.success('Tagged as subscription');
  };

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-display text-xl font-bold">Edit Transaction</h1>
        {isSynced && (
          <span className="ml-auto rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Synced</span>
        )}
      </div>

      <div className="space-y-4">
        {/* Quick tagging for synced transactions */}
        {isSynced && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5"
              onClick={handleAddAsSubscription}
            >
              <CreditCard className="h-4 w-4" />
              Tag as Subscription
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5"
              onClick={handleAddAsBill}
            >
              <Receipt className="h-4 w-4" />
              Add as Bill
            </Button>
          </div>
        )}

        <div className="flex gap-2">
          <Button type="button" variant={type === 'expense' ? 'default' : 'outline'} className="flex-1" onClick={() => setType('expense')}>Expense</Button>
          <Button type="button" variant={type === 'income' ? 'default' : 'outline'} className="flex-1" onClick={() => setType('income')}>Income</Button>
        </div>

        <div>
          <Label>Amount (AUD)</Label>
          <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="h-12 text-lg" />
        </div>

        <div>
          <Label>Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-12" />
        </div>

        <div>
          <Label>Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.categories.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Description</Label>
          <Input value={description} onChange={e => setDescription(e.target.value)} className="h-12" />
        </div>

        <div className="flex items-center justify-between rounded-lg border p-3">
          <Label className="cursor-pointer">Tax deductible</Label>
          <Switch checked={taxDeductible} onCheckedChange={setTaxDeductible} />
        </div>

        {accounts.length > 0 && (
          <div>
            <Label>Account</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-12"><SelectValue placeholder="No account" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">No account</SelectItem>
                {accounts.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {type === 'income' && (
          <div>
            <Label>Super contribution</Label>
            <Input type="number" step="0.01" min="0" value={superContribution} onChange={e => setSuperContribution(e.target.value)} className="h-12" />
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} className="h-12 w-full text-base">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Changes
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="w-full gap-2">
              <Trash2 className="h-4 w-4" /> Delete Transaction
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove this {txn.type} of {formatAUD(txn.amount)}. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
